"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon, XCircleIcon, InfoIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/src/components/shadcn/ui/drawer";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/shadcn/ui/tooltip";
import { getPersonName } from "@/src/models/person";
import { Practitioner } from "@/src/models/practitioner";
import { AgenticPractitionerRecommendation, AgenticRecommendation, Eligibility } from "@/src/models/scheduling-recommendation";
type PractitionerRecommendation = {
  npiNumber: string;
  name: string;
  isReceivingProvider: boolean;
  agentic?: AgenticPractitionerRecommendation;
};
const SCENARIOS = [{
  key: "newPatientScheduling",
  label: "New Patient"
}, {
  key: "establishedPatientScheduling",
  label: "Existing Patient"
}, {
  key: "rescheduling",
  label: "Rescheduling"
}] as const satisfies readonly {
  key: keyof AgenticPractitionerRecommendation;
  label: string;
}[];
function EligibilityIcon(t0) {
  const $ = _c(2);
  const {
    eligible
  } = t0;
  let t1;
  if ($[0] !== eligible) {
    t1 = eligible ? <CheckCircleIcon className="h-3 w-3 text-success" /> : <XCircleIcon className="h-3 w-3 text-destructive" />;
    $[0] = eligible;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function combineAndSortPractitioners(agenticRecommendation: AgenticRecommendation | null | undefined, receivingProvider: Practitioner | null | undefined): PractitionerRecommendation[] {
  const agenticList = agenticRecommendation?.evaluatedPractitioners ?? [];
  const receivingNpi = receivingProvider?.npiNumber;
  const byNpi = new Map(agenticList.map(p => [p.npiNumber, p]));
  const allNpis = [...new Set([...agenticList.map(p => p.npiNumber), ...(receivingNpi ? [receivingNpi] : [])])];
  const familyNameForSort = (name: string, person?: Practitioner | null) => person?.familyName ?? name.trim().split(/\s+/).pop() ?? "";
  return allNpis.map(npi => {
    const isReceiving = npi === receivingNpi;
    const name = isReceiving ? getPersonName(receivingProvider!) : byNpi.get(npi)?.providerDisplayName ?? "";
    return {
      npiNumber: npi,
      name,
      isReceivingProvider: isReceiving,
      agentic: byNpi.get(npi),
      familyNameForSort: familyNameForSort(name, isReceiving ? receivingProvider : undefined)
    };
  }).sort((a, b) => {
    if (a.isReceivingProvider) return -1;
    if (b.isReceivingProvider) return 1;
    return a.familyNameForSort.localeCompare(b.familyNameForSort);
  });
}
function ReasonList(t0) {
  const $ = _c(4);
  const {
    reasons
  } = t0;
  let t1;
  if ($[0] !== reasons) {
    t1 = reasons.map(_temp);
    $[0] = reasons;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="list-disc list-inside ml-2 text-xs text-muted-foreground space-y-1">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(reason, idx) {
  return <li key={idx} className="mb-1 last:mb-0">{reason}</li>;
}
function ReasonCodeBadges(t0) {
  const $ = _c(4);
  const {
    codes
  } = t0;
  let t1;
  if ($[0] !== codes) {
    t1 = codes.map(_temp2);
    $[0] = codes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex flex-wrap gap-1 ml-2">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(code) {
  return <span key={code} className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{code}</span>;
}
function PractitionerCard(t0) {
  const $ = _c(17);
  const {
    practitioner,
    compact
  } = t0;
  const agentic = practitioner.agentic;
  let t1;
  if ($[0] !== agentic || $[1] !== compact || $[2] !== practitioner.isReceivingProvider || $[3] !== practitioner.name) {
    let t2;
    if ($[5] !== agentic) {
      t2 = scenario => ({
        ...scenario,
        eligibility: agentic?.[scenario.key]
      });
      $[5] = agentic;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    const scenarios = SCENARIOS.map(t2).filter(_temp3);
    const hasScenarios = scenarios.length > 0;
    let t3;
    if ($[7] !== agentic || $[8] !== hasScenarios) {
      t3 = agentic && !hasScenarios && <span className="flex-shrink-0"><EligibilityIcon eligible={!!agentic.shouldSchedule} /></span>;
      $[7] = agentic;
      $[8] = hasScenarios;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    const t4 = compact ? "font-semibold text-background" : "text-sm font-semibold text-foreground";
    const t5 = practitioner.isReceivingProvider ? "(Receiving Provider)" : "";
    let t6;
    if ($[10] !== practitioner.name || $[11] !== t4 || $[12] !== t5) {
      t6 = <span className={t4}>{practitioner.name} {t5}</span>;
      $[10] = practitioner.name;
      $[11] = t4;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t3 || $[15] !== t6) {
      t7 = <div className="flex items-center gap-2">{t3}{t6}</div>;
      $[14] = t3;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t1 = <div className={`flex flex-col gap-2 border-b pb-2 last:border-b-0 last:pb-0 ${compact ? "gap-3 border-background/20 pb-3" : "border-border"}`}>{t7}{hasScenarios ? <div className="flex flex-col gap-1.5 ml-1">{scenarios.map(t8 => {
          const {
            key,
            label,
            eligibility
          } = t8;
          return <div key={key} className="flex flex-col gap-1"><div className="flex items-center gap-2"><span className="flex-shrink-0"><EligibilityIcon eligible={eligibility.eligible} /></span><span className={compact ? "text-xs text-background" : "text-xs font-medium text-foreground"}>{label}</span></div>{!compact && eligibility.reasons.length > 0 && <ReasonList reasons={eligibility.reasons} />}{!compact && eligibility.reasonCodes && eligibility.reasonCodes.length > 0 && <ReasonCodeBadges codes={eligibility.reasonCodes} />}</div>;
        })}</div> : !compact && <>{agentic?.reason && agentic.reason.length > 0 && <ReasonList reasons={agentic.reason} />}{agentic?.reasonCodes && agentic.reasonCodes.length > 0 && <ReasonCodeBadges codes={agentic.reasonCodes} />}</>}</div>;
    $[0] = agentic;
    $[1] = compact;
    $[2] = practitioner.isReceivingProvider;
    $[3] = practitioner.name;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function _temp3(scenario_0) {
  return scenario_0.eligibility != null;
}
export function SchedulingEligibility(t0) {
  const $ = _c(45);
  const {
    agenticRecommendation,
    receivingProvider
  } = t0;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== agenticRecommendation || $[1] !== drawerOpen || $[2] !== receivingProvider || $[3] !== tooltipOpen) {
    const combinedPractitioners = combineAndSortPractitioners(agenticRecommendation, receivingProvider);
    let t10;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = e => {
        e.stopPropagation();
        setDrawerOpen(true);
        setTooltipOpen(false);
      };
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    const openDrawer = t10;
    T1 = Drawer;
    t7 = drawerOpen;
    t8 = setDrawerOpen;
    const t11 = tooltipOpen && !drawerOpen;
    let t12;
    if ($[16] !== drawerOpen) {
      t12 = () => {
        if (!drawerOpen) {
          setTooltipOpen(true);
        }
      };
      $[16] = drawerOpen;
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    let t13;
    let t14;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = () => setTooltipOpen(false);
      t14 = <InfoIcon className="size-4 opacity-70 group-hover:opacity-100 transition-opacity" />;
      $[18] = t13;
      $[19] = t14;
    } else {
      t13 = $[18];
      t14 = $[19];
    }
    let t15;
    if ($[20] !== t12) {
      t15 = <TooltipTrigger asChild={true}><DrawerTrigger asChild={true}><button type="button" className="group size-4 inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" onClick={openDrawer} onMouseEnter={t12} onMouseLeave={t13}>{t14}</button></DrawerTrigger></TooltipTrigger>;
      $[20] = t12;
      $[21] = t15;
    } else {
      t15 = $[21];
    }
    const t16 = <TooltipContent side="left" className="max-w-xs p-3" onClick={openDrawer}><div className="flex flex-col gap-3">{combinedPractitioners.map(_temp4)}</div></TooltipContent>;
    if ($[22] !== t11 || $[23] !== t15 || $[24] !== t16) {
      t9 = <TooltipProvider delayDuration={0}><Tooltip open={t11} onOpenChange={setTooltipOpen}>{t15}{t16}</Tooltip></TooltipProvider>;
      $[22] = t11;
      $[23] = t15;
      $[24] = t16;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    T0 = DrawerContent;
    t4 = "right";
    t5 = "w-full max-w-2xl sm:w-3/4 flex flex-col p-0";
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <DrawerHeader className="flex-shrink-0 px-6 pt-6 pb-4"><DrawerTitle>Scheduling Eligibility</DrawerTitle></DrawerHeader>;
      $[26] = t6;
    } else {
      t6 = $[26];
    }
    t3 = "flex-1 overflow-y-auto px-6 pb-6";
    t1 = "flex flex-col gap-2";
    t2 = combinedPractitioners.map(_temp5);
    $[0] = agenticRecommendation;
    $[1] = drawerOpen;
    $[2] = receivingProvider;
    $[3] = tooltipOpen;
    $[4] = T0;
    $[5] = T1;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    T0 = $[4];
    T1 = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[27] !== t1 || $[28] !== t2) {
    t10 = <div className={t1}>{t2}</div>;
    $[27] = t1;
    $[28] = t2;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t3) {
    t11 = <div className={t3}>{t10}</div>;
    $[30] = t10;
    $[31] = t3;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== T0 || $[34] !== t11 || $[35] !== t4 || $[36] !== t5 || $[37] !== t6) {
    t12 = <T0 side={t4} className={t5}>{t6}{t11}</T0>;
    $[33] = T0;
    $[34] = t11;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== T1 || $[40] !== t12 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t13 = <T1 open={t7} onOpenChange={t8}>{t9}{t12}</T1>;
    $[39] = T1;
    $[40] = t12;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
}
function _temp5(p_0) {
  return <PractitionerCard key={p_0.npiNumber} practitioner={p_0} compact={false} />;
}
function _temp4(p) {
  return <PractitionerCard key={p.npiNumber} practitioner={p} compact={true} />;
}